import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, DownloadIcon, InfinityIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { CountdownTimer } from './CountdownTimer';
const MOCKUP = 'https://cdn.magicpatterns.com/uploads/aiNpLwdzBBD6ii83FrAqMy/MOCKUP_PRINCIPAL_PREGUNTAS.png';
interface HeroProps {
  onCta: () => void;
}
export function Hero({
  onCta
}: HeroProps) {
  const bullets = ['Más de 600 preguntas listas para aplicar.', '6 tomos organizados según el momento clínico.', '6 bonos exclusivos para resolver los bloqueos más comunes.'];
  return <header className="relative overflow-hidden bg-gradient-to-b from-[#35114f] via-[#4a1d6a] to-[#5b2580] px-4 pb-10 pt-8 text-white" data-id="element-120" data-mp="s9qa9x7cd5im">
      {/* soft glow accents */}
      <div className="pointer-events-none absolute -left-16 top-10 h-56 w-56 rounded-full bg-magenta/25 blur-3xl" data-id="element-121" data-mp="3d1sa6w93nhsl" />
      <div className="pointer-events-none absolute -right-16 top-40 h-56 w-56 rounded-full bg-lilac/25 blur-3xl" data-id="element-122" data-mp="381nwtf3hn36l" />

      <div className="relative mx-auto flex max-w-md flex-col items-center text-center" data-id="element-123" data-mp="3j9ahhe4fas5o">
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-5 flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-pink" data-id="element-124" data-mp="3o73ouahgps7j">
          ✦ El Oráculo de las Preguntas
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="text-sm font-medium text-white/70" data-id="element-125" data-mp="358rdqj6zq89b">
          Nunca vuelvas a preguntarte…
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mt-2 font-display text-4xl font-black leading-[1.05] sm:text-5xl" data-id="element-126" data-mp="2vj7tpa7jk8cv">
          <span className="italic text-white/90" data-id="element-127" data-mp="s1rpyhl8dur4">“¿Y ahora</span>
          <br data-id="element-128" data-mp="s3cloaja7jqk" />
          <span className="text-pink" data-id="element-129" data-mp="s3ur9xru3ab5">qué le pregunto?”</span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="mt-4 text-[15px] leading-relaxed text-white/80" data-id="element-130" data-mp="na0ab23m59fr">
          Descubre el sistema que reúne más de{' '}
          <span className="font-bold text-white" data-id="element-131" data-mp="1b15qjidgu3ql">600 preguntas estratégicas</span>, organizadas por
          etapa de la sesión y tipo de paciente, para iniciar, profundizar, desbloquear y cerrar
          cualquier proceso terapéutico con más seguridad, claridad y confianza.
        </motion.p>

        {/* mockup */}
        <motion.img src={MOCKUP} alt="El Oráculo de las Preguntas — 6 tomos y bonos" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.25,
        duration: 0.6
      }} className="animate-floaty mt-6 w-full max-w-sm drop-shadow-2xl" data-id="element-132" data-mp="x0csinnygqiq" />

        {/* bullets */}
        <ul className="mt-2 w-full space-y-2.5 text-left" data-id="element-133" data-mp="21amuh5blvkk8">
          {bullets.map(b => <li key={b} className="flex items-start gap-2.5" data-id="element-134" data-mp="26a99nkazvavo">
              <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-pink" data-id="element-135" data-mp="268og9y0f1xze" />
              <span className="text-sm font-medium text-white/90" data-id="element-136" data-mp="2b2ahmu14qffw">{b}</span>
            </li>)}
        </ul>

        {/* CTA */}
        <div id="hero-cta" className="mt-7 w-full" data-id="element-137" data-mp="2g25bn6g7bl56">
          <CtaButton onClick={onCta} data-id="element-138" data-mp="2kxkk1ivfwou7">Quiero el Oráculo de las Preguntas</CtaButton>
        </div>

        <div className="mt-5 w-full rounded-2xl bg-white/95 p-4 shadow-lg" data-id="element-139" data-mp="2p1sbhnta8iw4">
          <CountdownTimer data-id="element-140" data-mp="2u03i7poa6da4" />
        </div>

        {/* trust badges */}
        <div className="mt-5 grid w-full grid-cols-2 gap-2 text-[11px] font-semibold" data-id="element-141" data-mp="umhcm87bkdmr">
          {[{
          icon: DownloadIcon,
          label: 'Descarga inmediata'
        }, {
          icon: ZapIcon,
          label: 'Pago único'
        }, {
          icon: InfinityIcon,
          label: 'Acceso de por vida'
        }, {
          icon: ShieldCheckIcon,
          label: 'Garantía de 7 días'
        }].map(({
          icon: Icon,
          label
        }) => <div key={label} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/10 px-2 py-2 text-white/90" data-id="element-142" data-mp="e9jypxg05fth">
              <Icon className="h-4 w-4 text-pink" data-id="element-143" data-mp="98mkfvy455x9" />
              {label}
            </div>)}
        </div>
      </div>
    </header>;
}